/**
 * Learn's own bell.
 *
 * The same three rows also reach Cloud's bell, mirrored — and read state is per
 * product: marking one read here does not mark Cloud's copy. Review has the
 * same asymmetry, and docs/SECURITY.md says so in those words rather than this
 * component apologising for it.
 *
 * `ref` is a path the Worker wrote, and it is checked again here before it
 * becomes a destination (`refPath`). A stored string turning into navigation is
 * the one place in this app where that is worth doing twice.
 */

import { useEffect, useState } from 'react';
import type { NotificationWire } from '@wire';
import { notifications as bell } from '../api';
import { ds } from '../ds';
import { Link } from '../router';
import { HAIR, RADIUS, formatWhen, monoPlain, refPath } from '../theme';
import { useActions, useLoad } from '../useLoad';

/** One row's sentence. The kinds are closed — see `NotificationKind`. */
export function describe(n: NotificationWire): string {
  switch (n.kind) {
    case 'lms_assigned':
      return `${n.actor?.name ?? 'Someone'} assigned you ${n.subject}`;
    case 'lms_due_soon':
      return `${n.subject} is due soon`;
    case 'lms_overdue':
      return `${n.subject} is overdue`;
  }
}

/** How often the count is re-read while the app is open. */
const POLL_MS = 120_000;

export function Bell() {
  const { IconButton } = ds();
  const [open, setOpen] = useState(false);
  const { data, reload } = useLoad(() => bell.list(), 'Could not load your notifications.');
  const { act } = useActions(reload);

  useEffect(() => {
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const unread = data?.unread ?? 0;
  const rows = (data?.notifications ?? []).slice(0, 8);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton
        icon="Bell"
        label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        active={open}
        onClick={() => setOpen((v) => !v)}
      />
      {unread > 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 8,
            height: 8,
            borderRadius: 999,
            background: 'var(--signal)'
          }}
        />
      )}

      {open && (
        <>
          <button
            type="button"
            className="mv-scrim"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              zIndex: 41,
              width: 330,
              maxHeight: 420,
              overflowY: 'auto',
              padding: 8,
              display: 'grid',
              gap: 2,
              alignContent: 'start',
              background: 'var(--surface-card)',
              border: HAIR,
              borderRadius: RADIUS.card,
              boxShadow: '0 18px 44px rgba(40,28,45,.18)'
            }}
          >
            {rows.length === 0 ? (
              <p style={{ ...monoPlain, margin: 0, padding: 12 }}>Nothing new.</p>
            ) : (
              rows.map((n) => <Row key={n.id} n={n} onGo={() => setOpen(false)} />)
            )}

            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                borderTop: HAIR,
                marginTop: 4,
                paddingTop: 6
              }}
            >
              <Link to="/notifications" onClick={() => setOpen(false)}>
                <span style={{ ...monoPlain, padding: '4px 8px' }}>See all</span>
              </Link>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => void act(() => bell.read(), 'Marked as read.')}
                  style={{
                    marginLeft: 'auto',
                    border: 'none',
                    background: 'transparent',
                    ...monoPlain,
                    padding: '4px 8px'
                  }}
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </span>
  );
}

export function Row({ n, onGo }: { n: NotificationWire; onGo?: () => void }) {
  const to = refPath(n.ref);
  const body = (
    <span
      style={{
        display: 'grid',
        gap: 3,
        padding: '9px 10px',
        borderRadius: RADIUS.control,
        background: n.readAt === null ? 'var(--primary-soft)' : 'transparent'
      }}
    >
      <span style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>{describe(n)}</span>
      <span style={monoPlain}>{formatWhen(n.createdAt)}</span>
    </span>
  );

  // A row whose `ref` did not survive the shape check is still worth showing —
  // it says something happened — it just does not become a link.
  return to ? (
    <Link to={to} onClick={onGo} className="mv-row">
      {body}
    </Link>
  ) : (
    body
  );
}
